import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

const AGB = () => {
    return (
        <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-indigo-50 p-4 pt-24">
            <div className="max-w-4xl mx-auto">
                <Link
                    to="/"
                    className="inline-flex items-center text-gray-600 hover:text-indigo-600 mb-6 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Zurück zur Startseite
                </Link>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/60 p-8 md:p-12"
                >
                    <div className="flex items-center gap-3 mb-6">
                        <FileText className="w-8 h-8 text-indigo-600" />
                        <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                            Allgemeine Geschäftsbedingungen (AGB)
                        </h1>
                    </div>

                    <div className="prose prose-indigo max-w-none text-gray-700 space-y-6">

                        <section>
                            <h2 className="text-xl font-semibold mt-6">§1 Geltungsbereich</h2>
                            <p>
                                Diese Allgemeinen Geschäftsbedingungen (nachfolgend "AGB") gelten für die Nutzung der Lernplattform "Studytino" (nachfolgend "Plattform") durch den Nutzer (nachfolgend "Kunde"). Die Plattform wird betrieben von <strong>Arda Alici, Klosterweg 27, 6600 Reutte, Österreich</strong> (nachfolgend "Anbieter").
                            </p>
                            <p>
                                Abweichende Bedingungen des Kunden werden nicht anerkannt, es sei denn, der Anbieter stimmt ihrer Geltung ausdrücklich zu.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-xl font-semibold mt-6">§2 Vertragsschluss</h2>
                            <p>
                                <strong>2.1 Kostenlose Nutzung</strong><br />
                                Mit der Registrierung auf der Plattform kommt ein kostenloser Nutzungsvertrag zwischen dem Kunden und dem Anbieter zustande. Der Kunde ist für die Richtigkeit seiner bei der Registrierung angegebenen Daten selbst verantwortlich.
                            </p>
                            <p>
                                <strong>2.2 Bezahltes Pro-Abonnement</strong><br />
                                Der Vertrag über das kostenpflichtige Pro-Abonnement kommt zustande, sobald der Kunde den Bestellvorgang abschließt und die Zahlung über Stripe erfolgreich bestätigt wurde.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-xl font-semibold mt-6">§3 Leistungen und Kosten</h2>
                            <p>
                                <strong>3.1 Kostenlose Basis‑Nutzung</strong><br />
                                Die grundlegende Nutzung der Plattform (3 KI-Generierungen pro Tag, max. 100 Karten pro Generierung) ist kostenlos.
                            </p>
                            <p>
                                <strong>3.2 Bezahlte Premium‑Funktionen („Pro“)</strong><br />
                                Für erweiterte Funktionen (höhere Nutzungslimits, Smart‑Lernmodus und größere Uploads) wird ein monatliches Abonnement angeboten. Der monatliche Preis beträgt <strong>6,99 € pro Monat</strong>. Gemäß § 6 Abs. 1 Z 27 UStG wird keine Umsatzsteuer berechnet. Die Abrechnung erfolgt über den Zahlungsdienstleister <strong>Stripe</strong>. Der Kunde ermächtigt den Anbieter, die fälligen Beträge von der hinterlegten Zahlungsmethode einzuziehen.
                            </p>
                            <p>
                                <strong>3.3 Leistungsumfang</strong><br />
                                Der Anbieter stellt dem Kunden eine webbasierte Anwendung zur Verfügung, mit der Karteikarten erstellt, verwaltet und gelernt werden können. Der Anbieter bemüht sich um eine hohe Verfügbarkeit, übernimmt jedoch keine Gewähr für eine unterbrechungsfreie Erreichbarkeit.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-xl font-semibold mt-6">§4 Nutzungsrechte</h2>
                            <p>
                                Der Kunde erhält ein einfaches, nicht übertragbares Recht zur Nutzung der Plattform für persönliche Lernzwecke. Eine gewerbliche Nutzung oder die Nutzung für kommerzielle Schulungs‑/Nachhilfeangebote ist nur nach vorheriger schriftlicher Zustimmung des Anbieters gestattet.
                            </p>
                            <p>
                                Die Inhalte der Plattform (Design, Quellcode, Grafiken) sind urheberrechtlich geschützt. Der Kunde darf keine Teile davon ohne Zustimmung des Anbieters vervielfältigen, verbreiten oder öffentlich zugänglich machen.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-xl font-semibold mt-6">§5 Pflichten des Kunden</h2>
                            <ul>
                                <li>Der Kunde stellt sicher, dass seine Zugangsdaten (Passwort) geheim gehalten werden.</li>
                                <li>Der Kunde verwendet die Plattform nicht für rechtswidrige, beleidigende oder belästigende Zwecke.</li>
                                <li>Der Kunde darf keine Inhalte hochladen, die Rechte Dritter (z. B. Urheberrechte) verletzen.</li>
                                <li>Der Kunde darf keine automatisierten Abfragen oder Skripte verwenden, die die Plattform übermäßig belasten.</li>
                                <li>Der Kunde darf die KI‑Generierungsfunktionen nicht missbräuchlich nutzen (z. B. automatisiertes Ausspielen von Inhalten).</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-xl font-semibold mt-6">§6 Kündigung und Zahlungsbedingungen</h2>
                            <p>
                                <strong>6.1 Kündigung durch den Kunden</strong><br />
                                Das Abonnement kann jederzeit mit Wirkung zum Ende der aktuellen Abrechnungsperiode gekündigt werden. Die Kündigung erfolgt über das Stripe‑Customer‑Portal oder durch schriftliche Erklärung an den Anbieter. Die Kündigung beendet das Abonnement zum Ende des bereits bezahlten Abrechnungszeitraums. Eine Rückerstattung bereits gezahlter Beträge erfolgt nur, soweit dies gesetzlich vorgeschrieben ist.
                            </p>
                            <p>
                                <strong>6.2 Kündigung durch den Anbieter</strong><br />
                                Der Anbieter ist berechtigt, das Abonnement mit sofortiger Wirkung zu kündigen oder den Account zu sperren, wenn der Kunde gegen diese AGB verstößt, die Plattform missbräuchlich nutzt oder die KI‑Generierungsfunktionen übermäßig belastet. In diesem Fall werden bereits gezahlte Beträge anteilig (pro rata temporis) erstattet.
                            </p>
                            <p>
                                <strong>6.3 Verfügbarkeit</strong><br />
                                Der Anbieter bemüht sich um eine hohe Verfügbarkeit der Plattform, kann jedoch keine unterbrechungsfreie Erreichbarkeit garantieren. Wartungsarbeiten werden, wenn möglich, angekündigt.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-xl font-semibold mt-6">§7 Haftung</h2>
                            <p>
                                Der Anbieter haftet für Vorsatz und grobe Fahrlässigkeit sowie für Schäden aus der Verletzung von Leben, Körper oder Gesundheit. Für leichte Fahrlässigkeit haftet der Anbieter nur bei Verletzung einer wesentlichen Vertragspflicht (Kardinalpflicht), jedoch beschränkt auf den vertragstypischen, vorhersehbaren Schaden. Die vorstehenden Haftungsbeschränkungen gelten nicht bei Arglist oder bei zwingenden gesetzlichen Haftungstatbeständen.
                            </p>
                            <p>
                                Der Anbieter übernimmt keine Gewähr für die Richtigkeit der von der KI generierten Inhalte. Die Nutzung der generierten Karteikarten erfolgt auf eigene Gefahr des Kunden.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-xl font-semibold mt-6">§8 Salvatorische Klausel</h2>
                            <p>
                                Sollten einzelne Bestimmungen dieser AGB unwirksam sein oder werden, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt. Anstelle der unwirksamen Bestimmung tritt die gesetzliche Regelung.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-xl font-semibold mt-6">§9 Änderungen der AGB</h2>
                            <p>
                                Der Anbieter behält sich vor, diese AGB mit Wirkung für die Zukunft zu ändern. Der Kunde wird über Änderungen per E‑Mail oder beim nächsten Login rechtzeitig informiert. Änderungen gelten nur, wenn der Kunde diesen ausdrücklich zustimmt, sofern gesetzlich erforderlich. Die aktuelle Fassung der AGB ist jederzeit auf der Plattform abrufbar.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-xl font-semibold mt-6">§10 Gerichtsstand und anwendbares Recht</h2>
                            <p>
                                Es gilt das Recht der Republik Österreich unter Ausschluss des UN‑Kaufrechts. Für Streitigkeiten mit Verbrauchern gilt der gesetzliche Gerichtsstand; für Streitigkeiten mit Unternehmern wird der Gerichtsstand Reutte (Österreich) vereinbart.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-xl font-semibold mt-6">§11 Widerrufsrecht für Verbraucher</h2>
                            <p>
                                Verbraucher haben das Recht, binnen vierzehn Tagen ohne Angabe von Gründen den Vertrag zu widerrufen. Die Widerrufsfrist beträgt vierzehn Tage ab dem Tag des Vertragsabschlusses.
                            </p>
                            <p>
                                Um das Widerrufsrecht auszuüben, muss der Kunde eine eindeutige Erklärung (z. B. per E‑Mail an <a href="mailto:tynorra00@gmail.com" className="text-indigo-600 hover:underline">tynorra00@gmail.com</a>) an den Anbieter senden. Die Frist ist gewahrt, wenn der Kunde die Erklärung vor Ablauf der Frist absendet.
                            </p>
                            <p>
                                <strong>Besondere Bedingungen für digitale Dienstleistungen:</strong><br />
                                Bei digitalen Dienstleistungen beginnt die Leistungserbringung unmittelbar nach Vertragsschluss. Der Kunde stimmt ausdrücklich zu, dass die Leistung vor Ablauf der Widerrufsfrist beginnt. Das Widerrufsrecht erlischt, sobald die digitale Leistung vollständig erbracht wurde.
                            </p>
                        </section>

                        <p className="text-sm text-gray-500 mt-8">Stand: Juli 2026</p>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default AGB;